import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";

import {Button, CircularProgress, Stack} from "@mui/material";
import Grid from "@mui/material/Grid";
import Page from "../ui/Page";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import Backdrop from "@mui/material/Backdrop";
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ConfirmModal from "../ui/ConfirmModal";
import {
    PersistedDataGridPremium,
    dateValueFormatter,
    dateValueGetter,
    serializeGridQuery,
    toast
} from "@lb/mlib-components";
import {useGridApiContext} from "@mui/x-data-grid-premium";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ZiggyContext from "../../store/ziggy-context";
import {NameEditInputCell, setCellSaving} from "../shipments/columns";

function NoteCell({params}) {
    const apiRef = useGridApiContext();

    const startEdit = (e) => {
        e.stopPropagation();
        apiRef.current.startCellEditMode({id: params.id, field: params.field});
    };

    return (
        <Stack direction={"row-reverse"} onClick={startEdit} sx={{
            alignItems: "center",
            width: "100%",
            height: "100%",
            cursor: "pointer"
        }}>
            <IconButton sx={{color: '#afaeae'}} size={"small"} tabIndex={-1}>
                <EditIcon fontSize='inherit'/>
            </IconButton>
            <Typography variant={"body2"}>{params.row.note}</Typography>
        </Stack>
    );
}

export default function Reconcile() {
    const ziggyCtx = useContext(ZiggyContext);
    const columns = useMemo(() => [
        {field: 'id', type: 'string', disablePadding: false, headerName: 'ID #', width: 120},
        {field: 'sku', type: 'string', disablePadding: false, headerName: 'SKU', width: 120},
        {field: 'lot', type: 'string', disablePadding: false, headerName: 'Lot', width: 120},
        {field: 'qty', type: 'number', disablePadding: false, headerName: 'Quantity', width: 120},
        {
            field: 'created', type: 'date', disablePadding: false, headerName: 'Created', width: 120,
            valueFormatter: (value) => value ? dateValueFormatter(dateValueGetter(value)) : 'never',
        },
        {
            field: 'note', type: 'string', disablePadding: false, headerName: 'Note', width: 300,
            editable: true,
            renderCell: (params) => <NoteCell params={params}/>,
            renderEditCell: (params) => <NameEditInputCell {...params}/>,
        },
    ], []);

    const [loading, setLoading] = useState(false);
    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

    function loadDocumentFromServer() {
        setLoading(true)

        axios.get(`/export/reconcile`, {
            responseType: 'blob',
            /*headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authCtx.token}`,
            }*/
        }).then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'report.xlsx'); //or any other extension
            document.body.appendChild(link);

            link.click();


        }).catch(() => {
        }).finally(() => setLoading(false))
    }

    const [refreshKey, setRefreshKey] = useState(0);
    useEffect(() => setRefreshKey((k) => k + 1), [ziggyCtx.load]);

    const onConfirm = useCallback(() => {

        return axios.delete('/reconcile')
            .then(() => {
                toast.success('Your request has ben successfully sent.');
            }).finally(() => {
                setOpenConfirmationModal(false)
                setRefreshKey((k) => k + 1)
            })

    }, []);

    const fetchFn = useCallback((gridState) =>
        axios.get(`/reconcile?${serializeGridQuery({...gridState, columns})}`)
            .then(r => ({rows: r.data.items ?? [], rowCount: r.data.total ?? 0})),
        [columns]);

    const exportFn = useCallback(() =>
        axios.get('/export/reconcile', {responseType: 'blob'})
            .then(res => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'reconcile-export.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
            }),
        []);

    const processRowUpdate = useCallback((newRow) => new Promise((resolve, reject) => {
        setCellSaving(newRow.id, 'note', true);

        axios.post('/reconcile', {inventoryid: newRow.id, note: newRow.note})
            .then(() => resolve(newRow))
            .catch(reject)
            .finally(() => setCellSaving(newRow.id, 'note', false));
    }), []);

    const handleProcessRowUpdateError = useCallback(() => {
        toast.error('There was an error processing your request.');
    }, []);

    return (
        <Page>
            <Backdrop
                sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
                open={loading}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>
            <ConfirmModal open={openConfirmationModal}
                          mainTitle={'Purge'}
                          title="Please confirm this purge."
                          confirmLabel="Purge"
                          danger
                          onClose={() => setOpenConfirmationModal(false)}
                          onConfirm={onConfirm}
            />
            <div>
                <div style={{height: "75vh", width: "100%"}}>
                    <PersistedDataGridPremium storageKey="reconcile" columns={columns}
                                              fetchFn={fetchFn}
                                              exportFn={exportFn}
                                              getRowId={row => row.id}
                                              processRowUpdate={processRowUpdate}
                                              onProcessRowUpdateError={handleProcessRowUpdateError}
                                              refreshKey={refreshKey}
                                              disableAggregation
                                              disableRowGrouping
                                              disablePivoting
                                              toolbar={
                        <Grid size={2}>
                            <Stack direction={"row"} spacing={1}>
                                <Button variant={"contained"} startIcon={<CleaningServicesIcon/>}
                                        onClick={() => {
                                            setOpenConfirmationModal(true)
                                        }}>
                                    Purge
                                </Button>
                                <Button variant={"outlined"} startIcon={<DownloadIcon/>}
                                        onClick={loadDocumentFromServer}>
                                    Report
                                </Button>
                            </Stack>
                        </Grid>
                                              }
                    />

                </div>
            </div>
        </Page>
    );
};
